import { useCallback } from 'react';
import type { FlatListProps } from 'react-native';
import { bridge, getRangeForEvent, isNil, isNotNil } from 'shared/src/utils';
import type { GoodsData } from '@banhala/ui-kit';
import type { MarketHomeGoodsItem } from 'shared/src/api';
import {
  retainedStateFamily,
  useRetainedRef,
} from '../../../../shared/src/hooks/retainedState';

const SCREEN_NAME = 'MARKET';
const loggedIndicesFamily = retainedStateFamily<string, number[]>(() => []);

export const useLogDisplayGoods = (params: {
  list_id: string | null;
  list_params?: string;
}) => {
  const goodsItemLoggedList = useRetainedRef(
    loggedIndicesFamily(`MarketHome-${params.list_id}`),
  );

  const logDisplayGoods = useCallback<
    NonNullable<
      FlatListProps<GoodsData & MarketHomeGoodsItem>['onViewableItemsChanged']
    >
  >(
    ({ viewableItems }) => {
      viewableItems.forEach(({ index, isViewable, item }) => {
        if (isNil(index) || !isViewable) {
          return;
        }
        if (isNil(params.list_id)) {
          return;
        }
        if (goodsItemLoggedList.current.includes(index)) {
          return;
        }
        goodsItemLoggedList.current.push(index);

        bridge.logAnalyticsEvent({
          channel: ['amplitude', 'ablylog', 'firebase'],
          name: 'DISPLAY_GOODS',
          analytics: {
            SCREEN: SCREEN_NAME,
            CATEGORY_DEPTH: item.item.category?.depth,
            CATEGORY_NAME: item.item.category?.name,
            CATEGORY_SNO: item.item.category?.sno.toString(),
            DELIVERY_TYPE: item.item.delivery_type,
            DISCOUNT_RATE: item.item.discount_rate,
            GOODS_NAME: item.item.name,
            GOODS_SNO: item.sno.toString(),
            IMAGE_BADGE_LIST: item.render.data.image_badge_list.map(
              ({ type }) => type,
            ),
            IS_AD: isNotNil(item.item.ad) ? '1' : '0',
            IS_NEW: item.item.is_new ? '1' : '0',
            // item.likes_count가 있으면 item.likes_count를 사용해야한다.
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT: null,
            // item.likes_count로 500 단위로 끊어서 전송 (2,001~2,500)
            // 해당 페이지는 item.likes_count가 없어서 null로 처리
            LIKES_COUNT_RANGE: null,
            LIST_ID: params.list_id,
            LIST_INDEX: index,
            LIST_PARAMS: params.list_params,
            MARKET_NAME: item.item.market.name,
            MARKET_SNO: item.item.market.sno.toString(),
            MARKET_TYPE: item.item.market.market_type.name,
            ORIGINAL_PRICE:
              item.item.first_page_rendering?.original_price ??
              item.item.linked_option.original_price,
            REVIEW_COUNT: item.item.total_review_count,
            REVIEW_COUNT_RANGE: getRangeForEvent({
              target: item.item.total_review_count,
              rangeUnit: 50,
            }),
            REVIEW_RATING: item.item.positive_review_rate,
            SALES_PRICE: item.item.price,
            SALES_PRICE_RANGE: getRangeForEvent({
              target: item.item.price,
              rangeUnit: 5000,
            }),
            STANDARD_CATEGORY_NAME: item.item.standard_category.name,
            STANDARD_CATEGORY_SNO: item.item.standard_category.sno.toString(),
            MARKET_TYPE_SNO: item.item.market.market_type.sno.toString(),
            INDEX: index,
          },
        });
      });
    },
    [params.list_id, params.list_params, goodsItemLoggedList],
  );

  return { logDisplayGoods };
};
